// Ports gc-status-chip.vue's status -> color mapping for use as PDF cell text color, for
// columns whose cellTemplate is 'Status'. The live component always renders white text on a
// colored badge background — there's no badge here, so the badge's background color is reused
// as the text color instead, which is the closest visual equivalent in a plain PDF cell.
//
// gc-status-chip.vue derives its CSS class from `status.toFriendlyStatus()` (a few short DB
// codes like 'A'/'C'/'X' map to friendly text; anything else passes through unchanged — see
// prototype-string.js), reusing that same global prototype here as the single source of truth
// for the code -> friendly-text step, so this can't drift from it. The friendly-text -> color
// step is a hand port of gc-status-chip.vue's <style> block, since colors are only expressed
// as CSS classes there and can't be read from JS at PDF-generation time.
const FRIENDLY_STATUS_COLORS = {
    Active: '#62d26f',
    Printed: '#62d26f',
    'In Stock': '#62d26f',
    'Awaiting Receipt': '#62d26f',
    'Awaiting Production': '#62d26f',
    Issued: '#62d26f',
    Available: '#62d26f',
    'In Progress': '#62d26f',
    Approved: '#62d26f',
    Planned: '#62d26f',
    Confirmed: '#62d26f',
    Compliant: '#62d26f',
    Reconciled: '#62d26f',
    Paid: '#62d26f',
    Sent: '#62d26f',
    Resolved: '#62d26f',

    Draft: '#eec300',
    'Not Compliant': '#eec300',
    Unpresented: '#eec300',
    'Uncommitted Reconciliation': '#eec300',
    'Awaiting Planning': '#eec300',
    Requested: '#eec300',
    New: '#eec300',
    Edit: '#eec300',

    Cancelled: '#e8384f',
    'Credit Hold': '#e8384f',
    Closed: '#e8384f',
    'Cash Before Despatch': '#e8384f',
    Inactive: '#e8384f',
    Incomplete: '#e8384f',
    'Not Reconciled': '#e8384f',
    Failed: '#e8384f',
    FAILED: '#e8384f',
    'Out of Stock': '#e8384f',
    Return: '#e8384f',
    Returned: '#e8384f',
    Credit: '#e8384f',
    Overdue: '#e8384f',
    Credited: '#e8384f',

    Completed: '#4186e0',
    Invoiced: '#4186e0',

    Locked: '#aa62e3',
    Assigned: '#aa62e3',
    'Pending Invoice': '#aa62e3',

    Pending: '#800080',
    Reserved: '#800080',

    Enquiry: '#ffa500',
    Accepted: '#ffa500',
    Queued: '#ffa500',
    QUEUED: '#ffa500',
    'Partially Received': '#ffa500',
    'In Production': '#ffa500',
    'Not Booked': '#ffa500',
    Open: '#ffa500',
    // 'On Hold' also satisfies gc-status-chip.vue's "unallocated" class condition — in the live
    // component both classes apply and CSS cascade order picks unallocated's color (#5b6b6b),
    // not this one, since .unallocated is declared after .onhold in that file's <style> block.
    // Kept here for documentation; getStatusColor() below resolves 'On Hold' to #5b6b6b to match.

    Allocated: '#38b0e8',
    'Committed with Errors': '#38b0e8',
    Offered: '#38b0e8',
    Investigating: '#38b0e8',

    Unallocated: '#5b6b6b', // alpha channel dropped (#5b6b6ba1) — jsPDF text color has no alpha
    'On Hold': '#5b6b6b',

    Notified: '#04c3a9',
    Planning: '#8b5cf6',
    Review: '#ff6f61',
};

// gc-status-chip.vue checks these against the RAW status string, not .toFriendlyStatus() — kept
// separate since toFriendlyStatus() wouldn't otherwise map them (they aren't recognised codes).
const RAW_STATUS_COLORS = {
    Complete: '#4186e0',
    Refunded: '#4186e0',
    Committed: '#4186e0',
    Reversed: '#e8384f',
    Prepared: '#eec300',
    Reversal: '#eec300',
    Notified: '#04c3a9',
};

export function getStatusColor(rawValue) {
    if (rawValue === null || rawValue === undefined || rawValue === '') return null;
    const raw = String(rawValue);

    if (raw.toLocaleLowerCase() === 'success') return '#62d26f';
    if (RAW_STATUS_COLORS[raw]) return RAW_STATUS_COLORS[raw];

    const friendly = raw.toFriendlyStatus();
    return FRIENDLY_STATUS_COLORS[friendly] || null;
}
